import { useState, useMemo } from "react";

/**
 * Custom hook to manage client-side table sorting state and logic.
 * @param {Array} items - The initial array of items to sort.
 * @param {string} initialSortKey - The key of the initial column to sort by.
 * @param {'asc' | 'desc'} initialSortDirection - The initial sort direction.
 * @returns {{sortedItems: Array, handleSort: Function, sortKey: string, sortDirection: 'asc' | 'desc'}} - Sorted items and sorting controls.
 */
const useSortableTable = (
  items = [],
  initialSortKey = null,
  initialSortDirection = "asc"
) => {
  const [sortKey, setSortKey] = useState(initialSortKey);
  const [sortDirection, setSortDirection] = useState(initialSortDirection);

  const sortedItems = useMemo(() => {
    let sortableItems = [...items];
    if (sortKey !== null) {
      sortableItems.sort((a, b) => {
        const valA = a[sortKey];
        const valB = b[sortKey];

        // Attempt numeric comparison first
        const numA = Number(valA);
        const numB = Number(valB);

        let comparison = 0;
        if (!isNaN(numA) && !isNaN(numB)) {
          // Numeric comparison
          if (numA < numB) {
            comparison = -1;
          } else if (numA > numB) {
            comparison = 1;
          }
        } else {
          // Fallback to string comparison if not numeric or NaN
          const strA = String(valA ?? "").toLowerCase(); // Handle null/undefined
          const strB = String(valB ?? "").toLowerCase(); // Handle null/undefined
          if (strA < strB) {
            comparison = -1;
          } else if (strA > strB) {
            comparison = 1;
          }
        }

        return sortDirection === "desc" ? comparison * -1 : comparison;
      });
    }
    return sortableItems;
  }, [items, sortKey, sortDirection]);

  const handleSort = (key) => {
    if (key === sortKey) {
      // Toggle direction if same key is clicked
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      // Set new key and default direction (e.g., 'asc' or 'desc')
      setSortKey(key);
      setSortDirection("desc"); // Default to descending for new key clicks
    }
  };

  return { sortedItems, handleSort, sortKey, sortDirection };
};

export default useSortableTable;
